// Resolve the internal-linking props for a page (Audit C H1). Pure + serializable
// (no hooks, no dataset read) so BOTH the client dispatch (RenderSections.js) and
// the server dispatch (RenderSectionsServer.jsx) call it with the same signature.
//
// Identity is resolved from whatever the dispatcher has:
//   • CLIENT path always has the bare `slug` (product buy pages + blog posts route
//     through the client island) -> familyBySlug / familyByArticleSlug (robust).
//   • SERVER path (hardware overview / specs, 0 client JS) has no slug, so we
//     recover the family from the sections: a `specsPage`/checkout `itemType`, or
//     the hero section's title text.
// Returns null when the page belongs to no product line (most pages) — the
// dispatch renders nothing extra, so non-product pages are unaffected.

import {
  familyBySlug,
  familyByItemType,
  familyByHeroTitle,
  familyByArticleSlug,
  nodeRoleForSlug,
  ARTICLES,
} from './relatedData'
import { normalizeInternalHref } from '../../../utils/regions'

// Pull plain text out of a string or a Sanity portable-text `title` (bounded).
function extractText(value, depth = 0) {
  if (value == null || depth > 6) return ''
  if (typeof value === 'string') return value
  if (Array.isArray(value)) return value.map((v) => extractText(v, depth + 1)).filter(Boolean).join(' ')
  if (typeof value === 'object') {
    if (typeof value.text === 'string') return value.text
    if (Array.isArray(value.children)) return extractText(value.children, depth + 1)
  }
  return ''
}

// Server-path identity: scan the resolved sections for an itemType or a hero title.
function subjectFromSections(sections) {
  if (!Array.isArray(sections)) return { family: null, role: null }
  let itemType = null
  let hasSpecs = false
  let heroTitle = ''
  for (const s of sections) {
    if (!s || s.hidden) continue
    if (s._type === 'specsPage') hasSpecs = true
    if (!itemType && typeof s.itemType === 'string') itemType = s.itemType
    if (!heroTitle && typeof s._type === 'string' && /hero/i.test(s._type)) {
      heroTitle = extractText(s.title)
    }
  }
  let family = itemType ? familyByItemType(itemType) : null
  let role = family ? (hasSpecs ? 'specs' : 'overview') : null
  if (!family && heroTitle) {
    family = familyByHeroTitle(heroTitle)
    if (family) role = 'overview'
  }
  return { family, role }
}

const scope = (href, region) => normalizeInternalHref(href, region)

/**
 * @param {{ slug?: string, sections?: any[], region?: string|null }} args
 * @returns {null | { familyName, clusterHeading, cluster, productsHeading, products, articlesHeading, articles }}
 */
export function resolveRelated({ slug = null, sections = null, region = null } = {}) {
  let family = null
  let role = null

  if (slug) {
    family = familyBySlug(slug) || familyByArticleSlug(slug)
    if (family) role = nodeRoleForSlug(family, slug)
  }
  if (!family) {
    const fromSections = subjectFromSections(sections)
    family = fromSections.family
    role = fromSections.role
  }
  if (!family) return null

  const onArticle = role === 'article'

  // (c) the hardware-overview <-> buy <-> specs cluster, minus the current node.
  // `specs` may be null (a line with no /specs route) — filtered out here so we
  // never emit a dead anchor.
  const clusterNodes = [
    { role: 'overview', href: family.overview, label: `${family.name} — features & overview` },
    { role: 'buy', href: family.buy, label: `Buy the ${family.name}` },
    { role: 'specs', href: family.specs, label: `${family.name} — full technical specifications` },
  ]
  const cluster = clusterNodes
    .filter((n) => n.href && n.role !== role)
    .map((n) => ({ label: n.label, href: scope(n.href, region) }))
  // From a product page the cluster deepens engagement ("More about the …"); from
  // an article it drives the reader into the product line ("Explore the …").
  const clusterHeading = onArticle
    ? `Explore the ${family.name}`
    : `More about the ${family.name}`

  // (a) related products (descriptive anchor + true, specific reason-to-click).
  const selfHref = role && family[role] ? String(family[role]) : null
  const products = (family.relatedProducts || [])
    .filter((p) => p && p.href && p.href !== selfHref)
    .map((p) => ({ name: p.name, href: scope(p.href, region), blurb: p.blurb || '' }))
  const productsHeading = family.productsHeading || 'You may also like'

  // (b) related articles (blog <-> product), excluding the current article.
  const articles = (family.articleSlugs || [])
    .filter((a) => role !== 'article' || String(a) !== String(slug))
    .map((a) => {
      const meta = ARTICLES[a] || null
      return {
        title: meta ? meta.title : a,
        blurb: meta ? meta.blurb || '' : '',
        href: scope('/' + a, region),
      }
    })
    .filter((a) => a.title)
  // On a blog post the list is sibling articles ("Keep reading"); on a product
  // page it is the family's editorial explainers.
  const articlesHeading = onArticle
    ? 'Keep reading'
    : family.articlesHeading || 'From the VRS journal'

  if (!cluster.length && !products.length && !articles.length) return null
  return {
    familyName: family.name,
    clusterHeading,
    cluster,
    productsHeading,
    products,
    articlesHeading,
    articles,
  }
}
